// Booking contact phone.
//
// WHY THIS EXISTS: ONDC billing requires a phone number, and this app signs in
// with Google, which never supplies one (`auth.currentUser.phoneNumber` is always
// null). The old weebee build got it free from phone-OTP sign-in.
//
// An empty billing phone is not harmless. The transit DTOs do not mark
// BillingPhone required (ticketing/presentation/dto/dto.go), so it passes silently
// to the BPP — but BMRCL NACKs an empty billing *name* (30000), and
// normalizeBillingPhone's doc comment records that BMRCL validates the stored
// billing phone on CANCEL and rejects malformed ones (TRV11 50001). So an empty
// phone breaks cancellation and very likely confirm.
//
// Being operator-set is an advantage for a test tool: the number can be varied per
// run instead of being welded to the signed-in identity.
import { useCallback, useSyncExternalStore } from 'react';
import { config } from '@/lib/platform';

const STORAGE_KEY = 'wayq-tickets-contact-phone';

const listeners = new Set<() => void>();

function read(): string {
  try {
    return localStorage.getItem(STORAGE_KEY) ?? config.defaultContactPhone;
  } catch {
    // Private-mode / storage-disabled browsers.
    return config.defaultContactPhone;
  }
}

export function getContactPhone(): string {
  return read();
}

export function setContactPhone(value: string): void {
  try {
    localStorage.setItem(STORAGE_KEY, value.trim());
  } catch {
    /* ignore — the in-memory value still applies for this session */
  }
  listeners.forEach((l) => l());
}

/**
 * True when the value has enough digits to be a usable Indian contact number.
 * The backend strips non-digits (normalizeBillingPhone), so formatting is free —
 * only the digit count matters.
 */
export function isUsableContactPhone(value: string): boolean {
  return value.replace(/\D/g, '').length >= 10;
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Reactive read of the booking contact phone. */
export function useContactPhone(): [string, (value: string) => void] {
  const phone = useSyncExternalStore(subscribe, read, read);
  const set = useCallback((value: string) => setContactPhone(value), []);
  return [phone, set];
}
